import { useCallback, useEffect, useMemo, useState } from 'react'
import { Ban, Loader2 } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportVoidDiscountAuditReport,
  fetchVoidDiscountAuditReport,
  formatReportDateTime,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function VoidDiscountAuditReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchVoidDiscountAuditReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load void & discount audit.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportVoidDiscountAuditReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity =
    (report?.summary?.void_count ?? 0) > 0 ||
    (report?.summary?.refund_count ?? 0) > 0 ||
    (report?.summary?.discount_count ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Ban className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Void & discount audit</h2>
            <p className="text-sm text-muted-foreground">
              Pre-payment voids, refunds, and discounts applied on completed sales.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Voids"
              value={`${report.summary.void_count} · AED ${formatReportMoney(report.summary.void_amount)}`}
            />
            <SummaryCard
              label="Refunds"
              value={`${report.summary.refund_count} · AED ${formatReportMoney(report.summary.refund_amount)}`}
            />
            <SummaryCard
              label="Discounts"
              value={`${report.summary.discount_count} · AED ${formatReportMoney(report.summary.discount_total)}`}
            />
            {report.compare_summary ? (
              <SummaryCard
                label="Compare voids"
                value={String(report.compare_summary.void_count)}
              />
            ) : (
              <SummaryCard label="Period" value={`${report.from} → ${report.to}`} />
            )}
          </div>

          {report.by_discount_reason.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Discounts by reason
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Reason</th>
                    <th className="px-4 py-3 font-medium">Entries</th>
                    <th className="px-4 py-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_discount_reason.map((row) => (
                    <tr key={row.reason_label} className="border-t border-border/60">
                      <td className="px-4 py-3">{row.reason_label}</td>
                      <td className="px-4 py-3">{row.entry_count}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.discount_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Sale / CN</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                  <th className="px-4 py-3 font-medium">Staff</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No voids, refunds, or discounts in this period.
                    </td>
                  </tr>
                ) : (
                  <>
                    {report.voids.map((row) => (
                      <tr key={`void-${row.id}`} className="border-t border-border/60">
                        <td className="px-4 py-3 font-medium text-amber-700">Void</td>
                        <td className="px-4 py-3">{formatReportDateTime(row.occurred_at)}</td>
                        <td className="px-4 py-3">{row.sale_number}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.amount)}</td>
                        <td className="px-4 py-3">{row.reason ?? '—'}</td>
                        <td className="px-4 py-3">{row.staff_name ?? '—'}</td>
                      </tr>
                    ))}
                    {report.refunds.map((row) => (
                      <tr key={`refund-${row.id}`} className="border-t border-border/60">
                        <td className="px-4 py-3 font-medium text-destructive">Refund</td>
                        <td className="px-4 py-3">{formatReportDateTime(row.occurred_at)}</td>
                        <td className="px-4 py-3">{row.credit_note_number ?? row.sale_number}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.amount)}</td>
                        <td className="px-4 py-3">{row.reason ?? '—'}</td>
                        <td className="px-4 py-3">{row.staff_name ?? '—'}</td>
                      </tr>
                    ))}
                    {report.discounts.map((row) => (
                      <tr
                        key={`discount-${row.sale_id}-${row.discount_type}-${row.description}`}
                        className="border-t border-border/60"
                      >
                        <td className="px-4 py-3">{row.discount_type_label}</td>
                        <td className="px-4 py-3">{formatReportDateTime(row.occurred_at)}</td>
                        <td className="px-4 py-3">{row.sale_number}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.discount_amount)}</td>
                        <td className="px-4 py-3">{row.reason_label ?? row.description}</td>
                        <td className="px-4 py-3">{row.staff_name ?? '—'}</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
